import { PRICING_OPTION_LABELS } from '../../constants';

import type { OverrideBasis, OverrideSet, OverridesValues, SavedOverridesValues } from './types';

const VALID_BASES: ReadonlyArray<OverrideBasis> = ['vendor_revenue', 'room_nights'];

// Mirrors svc-offer `pricingToolDraftValidator.ts` override tier rules: each tier's
// `to` and `comms` are required numbers, `from` (when present) must be less than `to`,
// and tiers must be ordered by increasing `to`.
export function validateOverrides(values: OverrideSet): Array<string> {
  const errors: Array<string> = [];
  const touched = !isOverrideSetUntouched(values);

  if (!VALID_BASES.includes(values.basis)) {
    errors.push('Basis must be vendor_revenue or room_nights.');
  }

  let previousTo: number | undefined;
  values.tiers.forEach((tier, index) => {
    if (touched && !tier.name?.trim()) {
      errors.push(`Tier ${index + 1}: name is required.`);
    }

    if (typeof tier.to !== 'number' || !Number.isFinite(tier.to)) {
      errors.push(`Tier ${index + 1}: "to" is required and must be a number.`);
    }

    if (typeof tier.comms !== 'number' || !Number.isFinite(tier.comms)) {
      errors.push(`Tier ${index + 1}: "comms" is required and must be a number.`);
    }

    if (tier.from !== undefined && typeof tier.to === 'number' && tier.from >= tier.to) {
      errors.push(`Tier ${index + 1}: "from" must be less than "to".`);
    }

    if (previousTo !== undefined && typeof tier.to === 'number' && tier.to <= previousTo) {
      errors.push(`Tier ${index + 1}: tiers must be ordered by increasing "to".`);
    }

    previousTo = typeof tier.to === 'number' ? tier.to : previousTo;
  });

  return errors;
}

export function defaultTierName(index: number) {
  return `Tier ${index + 1}`;
}

// An autofilled name doesn't count as an edit; a basis change does.
export function isOverrideSetUntouched(set: OverrideSet): boolean {
  return (
    set.basis === 'vendor_revenue' &&
    set.tiers.every(
      (tier, index) =>
        (!tier.name?.trim() || tier.name === defaultTierName(index)) &&
        tier.from === undefined &&
        !tier.to &&
        !tier.comms &&
        tier.taxAdjustmentPct === undefined,
    )
  );
}

export function withoutUntouchedOverrides(overrides: OverridesValues): SavedOverridesValues {
  return {
    options: Object.fromEntries(Object.entries(overrides.options).filter(([, set]) => !isOverrideSetUntouched(set))),
  };
}

export function describeInvalidOverrides(overrides: OverridesValues): string | undefined {
  // Only what gets saved is validated; untouched options are dropped anyway.
  for (const [option, set] of Object.entries(withoutUntouchedOverrides(overrides).options)) {
    const [error] = validateOverrides(set);
    if (error) {
      return `${PRICING_OPTION_LABELS[Number(option)]} overrides: ${error}`;
    }
  }
  return undefined;
}
